import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { NotificationManager } from 'react-notifications';
import ModalComponent from '../../../../helper_components/ModalComponent';
import { useLoaderContext } from '../../../../layout/shared/loader_context';
import { useGlobalContext } from '../../../../../context/globalContext/GlobalContext';
import { PartiesService } from '../../../details_expedient/modals/partiesLocalModal/services/partiesServices';
import {
  fieldsValidation,
  getUsersToTransfer,
  transferExpedient,
} from '../../expedients_list_logic';
import InternalTurnForm from './InternalTurnForm';
import {
  INTERNAL_TURN_REQUIRED_FIELDS,
  buildInternalTurnPayload,
  getPartiesForInternalTurn,
} from './internalTurnLogic';

/**
 * CJJ-44: turnado interno que se ofrece al terminar la primera firma de una
 * multifirma. Usa el mismo formulario, destinatarios, validación y endpoint que
 * el turnado de "Mis Expedientes", y agrega "Saltar turnado" para dejar el
 * documento firmado sin turnar el expediente.
 *
 * - onTurned: se llama después de turnar con éxito (transferExpedient ya
 *   notifica y redirige como en "Mis Expedientes").
 * - onSkip: se llama al saltar el turnado o cerrar el modal.
 */
export const useInternalTurnModal = ({
  expedient = {},
  history = { push: () => {} },
  onTurned = async () => {},
  onSkip = async () => {},
}) => {
  const loader = useLoaderContext();
  const {
    dataRequest: {
      mattersList: { getMattersList, mattersList },
    },
  } = useGlobalContext();
  const [show, setShow] = useState(false);
  const [transfer, setTransfer] = useState({});
  const [receiverOptions, setReceiverOptions] = useState([]);
  const [partiesList, setPartiesList] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSending, setIsSending] = useState(false);

  const openInternalTurnModal = async () => {
    setErrors({});
    setIsSending(false);
    setTransfer({});
    getMattersList();
    // notShowUserLogged = true: sólo usuarios del mismo juzgado y sin el propio usuario.
    await getUsersToTransfer(
      () => setShow(true),
      setReceiverOptions,
      expedient,
      setTransfer,
      {},
      true,
      loader
    );
    setPartiesList(expedient.id ? await getPartiesForInternalTurn(expedient.id) : []);
  };

  const closeModal = () => {
    setShow(false);
    setTransfer({});
    setErrors({});
    PartiesService.handleResetCookie();
  };

  const handleSkip = async () => {
    closeModal();
    await onSkip();
  };

  const handleSubmit = async () => {
    const validation = fieldsValidation(INTERNAL_TURN_REQUIRED_FIELDS, transfer);
    if (typeof validation === 'object') {
      setErrors(validation);
      return;
    }

    try {
      loader.show(true);
      setIsSending(true);
      const success = await transferExpedient(
        buildInternalTurnPayload(transfer),
        { history },
        setErrors,
        false
      );
      if (!success) {
        setIsSending(false);
        return;
      }
      setShow(false);
      await onTurned();
    } catch (error) {
      setIsSending(false);
      NotificationManager.error(error.message);
    } finally {
      PartiesService.handleResetCookie();
      loader.show(false);
    }
  };

  return {
    openInternalTurnModal,
    internalTurnModalProps: {
      show,
      transfer,
      setTransfer,
      errors,
      receiverOptions,
      partiesList,
      mattersList,
      isSending,
      onSkip: handleSkip,
      onSubmit: handleSubmit,
    },
  };
};

const InternalTurnModal = ({
  show,
  transfer,
  setTransfer,
  errors,
  receiverOptions,
  partiesList,
  mattersList,
  isSending,
  onSkip,
  onSubmit,
}) => (
  <ModalComponent
    show={show}
    header={transfer['expedient_number'] ? transfer['expedient_number'] : 'Turnar expediente'}
    body={
      <InternalTurnForm
        transfer={transfer}
        setTransfer={setTransfer}
        errors={errors}
        receiverOptions={receiverOptions}
        partiesList={partiesList}
        mattersList={mattersList}
      />
    }
    footer={
      <>
        <Button variant="secondary" disabled={isSending} onClick={onSkip}>
          Saltar turnado
        </Button>
        <Button variant="primary" type="submit" disabled={isSending} onClick={onSubmit}>
          Turnar
        </Button>
      </>
    }
    canClose={!isSending}
    onHide={onSkip}
  />
);

export default InternalTurnModal;
